import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  DataTable,
  DataTableAction,
  DataTableBody,
  Group,
  Tooltip,
} from '@optiaxiom/react';
import { IconTriangleExclamation } from '@optiaxiom/icons';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { Link as RouterLink } from 'react-router-dom';
import type { AppItem } from '@/lib/api';
import { targetProductIntent } from './targetProduct';
import styles from './AppsPage.module.css';

interface AppsTableProps {
  apps: AppItem[];
  /** Shards rivendell couldn't reach; when non-empty, install counts are a lower bound. */
  degradedShards: string[];
}

const appHref = (appId: string) => `/apps/${encodeURIComponent(appId)}`;

export function AppsTable({ apps, degradedShards }: AppsTableProps) {
  const degraded = degradedShards.length > 0;
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<AppItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'App',
        // Columns are flex items whose final width is ~proportional to `size` (base width +
        // flex-grow both derive from it). App names are the primary content, so this column gets
        // the lion's share; without a size every column defaults to 150px, cramping the name.
        size: 480,
        // The name is the row's primary action: DataTable forwards a whole-row click to it,
        // and it stays a real link for keyboard users / cmd+click (open in new tab).
        cell: ({ row }) => (
          <DataTableAction primary asChild>
            <Box asChild className={styles.appNameLink}>
              <RouterLink to={appHref(row.original.appId)}>{row.original.name}</RouterLink>
            </Box>
          </DataTableAction>
        ),
      },
      {
        accessorKey: 'appId',
        header: 'App ID',
        size: 300,
        cell: ({ getValue }) => (
          <Badge
            intent="neutral"
            rounded="sm"
            border="1"
            variant="subtle"
            fontFamily="mono"
            textTransform="none"
          >
            {getValue<string>()}
          </Badge>
        ),
      },
      {
        accessorKey: 'targetProduct',
        header: 'Target product',
        size: 160,
        cell: ({ getValue }) => {
          const product = getValue<string>();
          return (
            <Badge intent={targetProductIntent(product)} variant="subtle">
              {product}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'installCount',
        size: 120,
        header: () => (
          <Group alignItems="center" gap="4">
            Installs
            {degraded && (
              <Tooltip
                content={`Some regions were unavailable (${degradedShards.join(
                  ', ',
                )}); install counts may be incomplete.`}
              >
                <Box asChild color="fg.warning">
                  <span>
                    <IconTriangleExclamation size={16} />
                  </span>
                </Box>
              </Tooltip>
            )}
          </Group>
        ),
        cell: ({ getValue }) => getValue<number>().toLocaleString(),
      },
    ],
    [degraded, degradedShards],
  );

  const table = useReactTable({
    data: apps,
    columns,
    // TanStack enables row selection by default, which makes DataTable treat a single click as
    // "select row" and defer navigation to a double click. We don't use selection, so turn it off
    // to get single-click row navigation via the primary action.
    enableRowSelection: false,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable table={table} className={`${styles.dataTable} ${styles.appsDataTable}`}>
      <DataTableBody />
    </DataTable>
  );
}
